import { Inject, Injectable } from '@angular/core';

import { ENV, IEnvironment } from 'env/config';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Service } from '../entity';
import { DataService } from '../http/dataService';

import { Result } from '../interfaces/helpers';
import { RepRosterResult } from '../interfaces/internal';
import {
  IRepRosterSalesRep,
  IRepRosterVendorGroup,
} from '../models/repRoster.model';
import { VendorSalesRep } from '../models/vendorSalesRep.model';

import { Repository } from './repository';

@Service({
  model: VendorSalesRep,
})
@Injectable({ providedIn: 'root' })
export class VendorSalesRepService extends Repository<VendorSalesRep> {
  constructor(@Inject(ENV) env: IEnvironment, dataService: DataService) {
    super({
      client: dataService,
      url: env.api.backend.reproster.endpoint as string,
    });
  }

  static getParams(queryParams?, conditions?, isCount = false) {
    let id;
    let whereClause = VendorSalesRep.whereClauses.vendorGroup;
    let whereOperator = 'equals';
    if (queryParams) {
      id = queryParams?.id || queryParams.vendorGroupId || '';
    } else if (conditions) {
      if (conditions.condition.indexOf('self') !== -1) {
        whereClause = VendorSalesRep.whereClauses.cpmsId;
      } else if (conditions.condition.indexOf('cpmsIDs') !== -1) {
        whereClause = VendorSalesRep.whereClauses.cpmsId;
        whereOperator = 'contains';
      } else if (conditions.condition.indexOf('store') !== -1) {
        whereClause = VendorSalesRep.whereClauses.storeId;
      } else if (conditions.condition.indexOf('vendorAccountName') !== -1) {
        whereClause = VendorSalesRep.whereClauses.vendorAccountName;
      } else if (conditions.condition.indexOf('vendorGroupId') !== -1) {
        whereClause = VendorSalesRep.whereClauses.vendorGroup;
      } else if (conditions.condition.indexOf('federationId') !== -1) {
        whereClause = VendorSalesRep.whereClauses.fedId;
      }

      id = conditions.value;
    }

    let soqlQuery = VendorSalesRep.getVendorSalesRepQuery(
      id,
      whereClause,
      whereOperator,
      isCount
    );

    if (!isCount && queryParams && queryParams.limit) {
      soqlQuery = `${soqlQuery} LIMIT ${queryParams.limit}`;
    }

    if (
      !isCount &&
      queryParams &&
      queryParams.page !== undefined &&
      queryParams.page !== 0
    ) {
      soqlQuery = `${soqlQuery} OFFSET ${queryParams.page * queryParams.limit}`;
    }

    return {
      q: soqlQuery,
    };
  }

  getCount(queryParams?: any, conditions?): Observable<RepRosterResult<any>> {
    const vendorGroupParamsCount = VendorSalesRepService.getParams(
      queryParams,
      conditions,
      true
    );

    const url = `${this.url}${this.api}`;
    return this.dataService.get<RepRosterResult<any>>(
      url,
      vendorGroupParamsCount
    );
  }

  getAll(queryParams?: any, conditions?): Observable<Result<VendorSalesRep>> {
    const vendorSalesRepParams = VendorSalesRepService.getParams(
      queryParams,
      conditions
    );

    const vendorSalesRepRequest = super.getAll(vendorSalesRepParams);

    if (!queryParams?.limit) {
      return vendorSalesRepRequest;
    } else {
      const countVendorSalesRepRequest = this.getCount(queryParams, conditions);

      return forkJoin([countVendorSalesRepRequest, vendorSalesRepRequest]).pipe(
        map((forkResult) => {
          const countResult = forkResult[0];
          const results = forkResult[1];

          return {
            items: results.items,
            links: results.links,
            meta: {
              itemCount: results.items.length,
              totalItems: countResult.totalSize,
              itemsPerPage: queryParams.limit,
              totalPages: Math.ceil(
                countResult.totalSize / results.items.length
              ),
              currentPage: 0,
            },
          };
        })
      );
    }
  }

  getOne(queryParams: any): Observable<VendorSalesRep> {
    const id = queryParams.id;
    const whereClause = VendorSalesRep.whereClauses.id;
    const whereOperator = 'equals';

    const salesRepParams = {
      q: VendorSalesRep.getVendorSalesRepQuery(id, whereClause, whereOperator),
    };

    return super
      .getOne<RepRosterResult<IRepRosterSalesRep>>(salesRepParams)
      .pipe(
        map((data: RepRosterResult<IRepRosterSalesRep>) => {
          const { records } = data;

          return records.map((vsr) => VendorSalesRep.createFromDTO(vsr))[0];
        })
      );
  }

  getMoreData(url: string) {
    const path = url.split('/');
    this.api = path[path.length - 1];

    return super.getAll().pipe(
      map((data: Result<VendorSalesRep>) => {
        this.api = '';
        return data;
      })
    );
  }

  createFromDTO(item: IRepRosterSalesRep): VendorSalesRep {
    return VendorSalesRep.createFromDTO(item);
  }

  private mapData(data: Result<IRepRosterSalesRep>) {
    return {
      ...data,
      items: data.items.map((vendorSalesRep) =>
        VendorSalesRep.createFromDTO(vendorSalesRep)
      ),
    };
  }
}
